import React, { useContext, useEffect, useState } from "react";
import AntdTable from "../../antd/Table/Table";
import trans from "../../../utils";
import { getAllData, editData, deleteDatabyId } from "../../../utils/IndexedDB";
import { Tag } from "antd";
import {
	useAddIRMutation,
	useConsultantQAIRMutation,
	useQaIRMutation,
	useResubmitIRMutation,
} from "../../../Redux/service/IR/IR";
import Button from "../../Helpers/Button/Button";
import ConstructionRequestHead from "./ConstructionRequestHandleHead";
import { toast } from "react-toastify";
import { popupcontext } from "../../../context/Popupscontext";
import { useBulkCreateSnagsMutation } from "../../../Redux/service/Snag/Snag";
import { useSelector } from "react-redux";

export default function ConstructionRequestTable({
	query,
	filter,
	currentPage,
	setCurrentPage,
	setFilter,
	handleQueryChange,
	configFilter,
	setConfigFilter,
}) {
	const inf = useSelector((state) => state.users.user);
	const { setReloadData } = useContext(popupcontext);
	const [tableData, setTableData] = useState(null);
	const [limit, setLimit] = useState(10);

	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);

	const [addIR, { isLoading: IRLoading }] = useAddIRMutation();

	const [bulkCreateSnags, { isLoading: SnagLoading }] =
		useBulkCreateSnagsMutation();
	const [addQAIR, { isLoading: IRQALoading }] = useQaIRMutation();
	const [ResubmitMutation, { isLoading: ResubmitLoading }] =
		useResubmitIRMutation();
	const [AddCQA, { isLoading: IRCONSLOADING, data }] =
		useConsultantQAIRMutation();

	let isLoading =
		IRLoading || SnagLoading || IRQALoading || ResubmitLoading | IRCONSLOADING;
	const fetchData = async () => {
		try {
			const data = await getAllData("OfflineModeData", filter);
			setTableData(data);
		} catch (error) {
			console.error("IndexedDB read error:", error);
		}
	};

	/* ------------------------------------------------ */
	/*  DELETE OLD SYNCED RECORDS (> 1 MINUTE)          */
	/* ------------------------------------------------ */
	const deleteOldSyncedRecords = async () => {
		try {
			const allData = await getAllData("OfflineModeData", filter);
			const currentTime = Date.now();
			const validTime = 24 * 60 * 60 * 1000; // 1 minute in milliseconds 60 * 1000

			// Find records that are synced and older than 1 minute
			const idsToDelete = allData
				.filter((item) => {
					return (
						item.statusSync === "Synced" &&
						item.syncTime &&
						currentTime - item.syncTime > validTime
					);
				})
				.map((item) => item.id);

			if (idsToDelete.length > 0) {
				// Delete all old synced records
				for (const id of idsToDelete) {
					await deleteDatabyId("OfflineModeData", filter, id);
				}

				// Show toast notification
				toast.info(
					`${trans("Deleted")} ${idsToDelete.length} ${trans("synced record(s)")}`,
					{
						autoClose: 2000,
						rtl: localStorage.getItem("lang") === "ar" ? true : false,
					},
				);

				// Refresh data after deletion
				await fetchData();
			}
		} catch (error) {
			console.error("Error deleting old synced records:", error);
		}
	};

	useEffect(() => {
		fetchData();
		// Check and delete old records on mount and when filter changes
		deleteOldSyncedRecords();
	}, [filter]);

	/* ------------------------------------------------ */
	/*  AUTO-CHECK EVERY MINUTE                         */
	/* ------------------------------------------------ */
	useEffect(() => {
		// Set up interval to check every minute
		const intervalId = setInterval(() => {
			deleteOldSyncedRecords();
		}, 60 * 1000); // Check every 1 minute

		// Cleanup interval on unmount
		return () => clearInterval(intervalId);
	}, [filter]);

	const statusColor = {
		"Pending Sync": "orange",
		Synced: "green",
		Failed: "red",
		Syncing: "blue",
	};

	/* ------------------------------------------------ */
	/*  API CALL PER ITEM                               */
	/* ------------------------------------------------ */
	const preparePayload = (item) => {
		if (!item.ir_number) {
			const {
				id,
				form_name,
				project_name,
				level_name,
				room_name,
				zone_name,
				syncTime,
				statusSync,
				...payload
			} = item;

			return payload;
		} else {
			const { lastFormData } = item;
			return { lastFormData };
		}
	};
	const prepareQAPayload = (item) => {
		if (item?.deficiencies) {
			const { comments, deficiencies, result } = item;
			return { comments, deficiencies, result };
		} else {
			const { correction_notes, ready_date, attachments } = item;
			return { correction_notes, ready_date, attachments };
		}
	};
	const prepareConsultantPayload = (item) => {
		const { comments, result } = item;
		return { comments, result };
	};
	const handleSingleSync = async (item, showToast = true) => {
		const formData = new FormData();
		let payload;
		switch (filter) {
			case "IR":
				payload = preparePayload(item);
				break;
			case "IRQA":
				if (inf.attributes.role === "qa") {
					payload = prepareQAPayload(item);
				} else if (inf.attributes.role === "consultant") {
					payload = prepareConsultantPayload(item);
				}
				break;
			default:
				payload = preparePayload(item);
		}

		if (filter === "Snag") {
			formData.append("project_id", payload.project_id);
			formData.append("zone_id", payload.zone_id);
			formData.append("level_id", payload.level);
			formData.append("room_id", payload.room_id);
			const snagsData = payload.snags.map((snag, index) => {
				// Append files for this snag
				if (snag.files && Array.isArray(snag.files) && snag.files.length > 0) {
					snag.files.forEach((file) => {
						if (file instanceof File) {
							formData.append(`snags[${index}][files][]`, file);
						}
					});
				}
				// Return snag data object (without files)
				return {
					sector: snag.sector,
					assigned_team_id: snag.assigned_to_id,
					subsystem: snag.subsystem || "",
					discipline: [snag.discipline]?.length || "",
					item: snag.item || "",
					description: snag.description || "",
					asset_tag_id: snag.asset_tag_id || "",
					drawing_ref: snag.drawing_ref || "",
					area_room: snag.area_room || "",
					location: snag.location || "",
					severity: snag.severity || "",
					acceptance_criteria: snag.acceptance_criteria || "",
					inspection_method: snag.inspection_method || "",
					location_details: snag.location_details || "",
					target_date: snag.target_date || "",
				};
			});

			formData.append("snags", JSON.stringify(snagsData));
		}
		if (filter === "IRQA" && inf.attributes.role === "qa") {
			if (payload.deficiencies?.length > 0) {
				payload.deficiencies?.forEach((def, index) => {
					if (def.files && Array.isArray(def.files) && def.files?.length > 0) {
						def.files.forEach((file) => {
							if (file instanceof File) {
								formData.append(`deficiencies[${index}][files][]`, file);
							}
							formData.append(`deficiencies[${index}][category]`, def.category);
							formData.append(`deficiencies[${index}][severity]`, def.severity);
							formData.append(
								`deficiencies[${index}][description]`,
								def.description,
							);
						});
					}
				});
				formData.append("result", payload.result);
			}
		}
		try {
			// set syncing

			for (let [key, value] of formData.entries()) {
				console.log(key, value);
			}
			await editData("OfflineModeData", filter, item.id, {
				...item,
				syncTime: null,
				statusSync: "Syncing",
			});
			switch (filter) {
				case "IR":
					console.log("Payload for IR:", payload);
					payload?.lastFormData
						? await ResubmitMutation({ id: item.uid, val: payload }).unwrap()
						: await addIR(payload).unwrap();

					toast.success(trans("IR Form Submitted Successfully"), {
						autoClose: 800,
						rtl: localStorage.getItem("lang") === "ar" ? true : false,
					});
					break;
				case "Snag":
					await bulkCreateSnags(formData).unwrap();
					toast.success(trans("Snag updated successfully"), {
						autoClose: 800,
						rtl: localStorage.getItem("lang") === "ar" ? true : false,
					});
					break;
				case "IRQA":
					const isQA = inf.attributes.role === "qa";
					const isConsultant = inf.attributes.role === "consultant";

					if (isQA) {
						console.log("IsQA");
						if (payload.correction_notes && payload.ready_date) {
							await ResubmitMutation({ id: item.uid, val: payload }).unwrap();
						} else if (payload.deficiencies?.length > 0) {
							await addQAIR({ id: item.uid, val: formData }).unwrap();
						} else {
							await addQAIR({ id: item.uid, val: payload }).unwrap();
						}
					} else if (isConsultant) {
						console.log("Payload", payload);
						console.log("Data of Api", data);
						await AddCQA({ id: item.uid, val: payload }).unwrap();
					}

					toast.success(
						trans(
							`IR ${isQA ? "QA" : "Consultant"} Review Submitted Successfully`,
						),
						{
							autoClose: 800,
							rtl: localStorage.getItem("lang") === "ar",
						},
					);

					break;

				default:
					await addIR(payload).unwrap();
			}
			// success
			await deleteDatabyId("OfflineModeData", filter, item.id);

			return { success: true };
		} catch (err) {
			// failed
			await editData("OfflineModeData", filter, item.id, {
				...item,
				syncTime: null,
				statusSync: "Failed",
			});

			if (showToast) {
				toast.error(`${err?.data?.message || trans("Sync failed")}`, {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				});
			}

			return { success: false, error: err };
		} finally {
			fetchData();
		}
	};

	/* ------------------------------------------------ */
	/*  SYNC SELECTED ONE BY ONE                        */
	/* ------------------------------------------------ */

	const handleSyncSelected = async () => {
		if (!selectedRows.length) return;

		for (const item of selectedRows) {
			await handleSingleSync(item);
		}

		setSelectedRowKeys([]);
		setSelectedRows([]);
	};

	/* ------------------------------------------------ */
	/*  TABLE COLUMNS                                   */
	/* ------------------------------------------------ */

	const Columns = [
		{
			title: trans(`${filter} Number`),
			dataIndex: "id",
			width: 200,
		},
		{
			title: trans("Configuration Name"),
			dataIndex: "configurationName",
			width: 200,
		},
		{
			title: trans("Project"),
			dataIndex: "project_name",
			width: 200,
			render: (text, record) => {
				return (
					<span>
						{record?.project_name || record?.project?.name || "- - - - - -"}
					</span>
				);
			},
		},
		{
			title: trans("Building"),
			dataIndex: "zone_name",
			width: 200,
			render: (text, record) => {
				return (
					<span>
						{record?.zone_name || record?.zone?.name || "- - - - - -"}
					</span>
				);
			},
		},
		{
			title: trans("Level"),
			dataIndex: "level_name",
			width: 200,
			render: (text, record) => {
				return (
					<span>
						{record?.level_name || record?.level?.name || "- - - - - -"}
					</span>
				);
			},
		},
		{
			title: trans("Space"),
			dataIndex: "room_name",
			width: 200,
			render: (text, record) => {
				return (
					<span>
						{record?.room_name || record?.room?.name || "- - - - - -"}
					</span>
				);
			},
		},
		{
			title: trans("Type"),
			dataIndex: "type",
			width: 150,
		},
		{
			title: trans("Status"),
			dataIndex: "statusSync",
			width: 150,
			render: (_, record) => (
				<Tag color={statusColor[record?.statusSync] || "default"}>
					{trans(record?.statusSync)}
				</Tag>
			),
		},
		{
			title: trans("Actions"),
			width: 150,
			render: (_, record) => {
				if (record.statusSync === "Failed") {
					return (
						<Button
							htmlType="button"
							style={{ cursor: "pointer" }}
							onClick={() => handleSingleSync(record)}
							img="New/OfflineMode/Spinner.svg"
						>
							{trans("Retry")}
						</Button>
					);
				}

				return (
					<Button
						disabled={
							record.statusSync === "Synced" || record.statusSync === "Syncing"
						}
						onClick={() => handleSingleSync(record)}
					>
						{trans("Sync")}
					</Button>
				);
			},
		},
	];

	/* ------------------------------------------------ */
	/*  FILTER DATA                                     */
	/* ------------------------------------------------ */
	const filteredData = (() => {
		let result =
			tableData
				?.filter((item) => item?.userId === inf?.id)
				?.filter(
					(i) => i.statusSync === "Pending Sync" || i.statusSync === "Failed",
				)
				?.slice()
				?.reverse() || [];

		if (query && query.trim()) {
			result = result.filter((item) =>
				Object.values(item).some((value) =>
					String(value).toLowerCase().includes(query.toLowerCase()),
				),
			);
		}

		if (configFilter) {
			result = result.filter((item) => {
				return item.configurationName === configFilter;
			});
		}

		return result;
	})();

	/* ------------------------------------------------ */
	/*  CHECKBOX SELECTION                              */
	/* ------------------------------------------------ */

	const rowSelection = {
		selectedRowKeys,
		onChange: (keys, rows) => {
			setSelectedRowKeys(keys);
			setSelectedRows(rows);
		},
		getCheckboxProps: (record) => ({
			disabled:
				record.statusSync === "Synced" || record.statusSync === "Syncing",
		}),
	};

	const getCurrentPageData = () => {
		const startIndex = (currentPage - 1) * limit;
		const endIndex = startIndex + limit;
		return filteredData?.slice(startIndex, endIndex) || [];
	};

	const currentPageData = getCurrentPageData();

	const pendingItems = currentPageData?.filter(
		(item) => item.statusSync === "Pending Sync",
	);

	const failedItems = currentPageData?.filter(
		(item) => item.statusSync === "Failed",
	);

	/* ------------------------------------------------ */
	/*  SYNC WITH PROGRESS TOAST                        */
	/* ------------------------------------------------ */
	const syncListSequential = async (list) => {
		if (!list?.length) return;

		const total = list.length;
		let completed = 0;
		let failed = 0;

		// Create initial loading toast
		const toastId = toast.info(`${trans("Syncing")}: ${completed}/${total}`, {
			rtl: localStorage.getItem("lang") === "ar" ? true : false,
		});

		for (const item of list) {
			const result = await handleSingleSync(item, false); // Don't show individual toasts

			completed++;

			if (!result.success) {
				failed++;
			}

			// Update toast with progress
			toast.update(toastId, {
				render: `${trans("Syncing")}: ${completed}/${total}`,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		}

		// Final toast based on results
		toast.dismiss(toastId);

		if (failed === 0) {
			toast.success(
				`${trans("All synced successfully")}: ${completed}/${total}`,
				{
					autoClose: 2000,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				},
			);
		} else if (failed === total) {
			toast.error(`${trans("All failed")}: ${failed}/${total}`, {
				autoClose: 2000,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		} else {
			toast.warning(
				`${trans("Completed")}: ${completed - failed}/${total} | ${trans("Failed")}: ${failed}`,
				{
					autoClose: 3000,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				},
			);
		}
		setReloadData(true);
	};

	const handleSyncPending = async () => {
		await syncListSequential(pendingItems);
	};

	const handleRetryFailed = async () => {
		await syncListSequential(failedItems);
	};

	return (
		<>
			<ConstructionRequestHead
				setFilter={setFilter}
				handleQueryChange={handleQueryChange}
				query={query}
				filter={filter}
				configFilter={configFilter}
				setConfigFilter={setConfigFilter}
				children={
					<div
						style={{
							display: "flex",
							gap: 10,
							justifyContent: "center",
							paddingBlock: "8px",
						}}
					>
						<Button
							disabled={!selectedRows.length || isLoading}
							onClick={handleSyncSelected}
						>
							{trans("Sync Selected")}
						</Button>

						<Button
							disabled={!pendingItems?.length || isLoading}
							style={{
								borderColor: pendingItems?.length > 0 && "Orange",
								color: pendingItems?.length > 0 && "Orange",
							}}
							onClick={handleSyncPending}
						>
							{trans("Sync Pending")}
						</Button>

						<Button
							disabled={!failedItems?.length || isLoading}
							danger={true}
							onClick={handleRetryFailed}
						>
							{trans("Retry Failed")}
						</Button>
					</div>
				}
			/>

			<div className="bg-white-rounded-top table-parent">
				<AntdTable
					columns={Columns}
					dataSource={filteredData || []}
					rowSelection={rowSelection}
					loading={false}
					pageSize={limit}
					totalItems={filteredData?.length || 0}
					currentPage={currentPage}
					onPageChange={(page) => setCurrentPage(page)}
					onLimitChange={(newLimit) => {
						setLimit(newLimit);
						setCurrentPage(1);
					}}
				/>
			</div>
		</>
	);
}
